import {
    ArrowDown,
    ArrowUp,
    Box,
    Caption,
    ErrorIcon,
    Heading,
    IconButton,
    ProgressBar,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    Warning,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import style from "./navigation-panel.scss";
import DataBearing from "components/icons/DataBearing";
import { isLimitApproaching } from "domain/asset-management/assets";
import { StoreState } from "store";

import testIds from "testIds.json";

interface Props {
    minimized: boolean;
}

const AssetsSummaryFooter = (props: Props) => {
    const { t } = useTranslation();
    const trackingSummary = useSelector((state: StoreState) => state.trackingSummaryReducer.trackingSummary);
    const [expanded, setExpanded] = React.useState<boolean>(true);

    if (trackingSummary === null) {
        return null;
    }

    const tracked = trackingSummary.trackedCount;
    const limit = trackingSummary.trackingLimit;
    const remaining = limit - tracked;
    const calculatePercentage = () => {
        return Math.floor((tracked * 100) / limit);
    };
    const renderSlotsLeft = () => {
        if (isLimitHit()) {
            return t("AssetManagement.navigationSidePanel.onHold", {
                count: trackingSummary.allDataBearingCount - trackingSummary.trackedCount,
            });
        }
        return t("AssetManagement.navigationSidePanel.slotsLeft", { count: remaining });
    };

    const renderLimit = () => {
        return t("AssetManagement.navigationSidePanel.limit", {
            currentCount: tracked,
            limit: limit,
        });
    };

    const isLimitHit = () => {
        return remaining === 0;
    };

    return (
        <Box
            testId={testIds.navigation.assets.dataBearingSummary.itself}
            className={style.assetSummaryContainer}
            columnLayout
            marginTop={"Xs"}
            padding={"Xs"}
        >
            {props.minimized ? (
                <Tooltip placement={"right"}>
                    <TooltipContent>
                        <Box columnLayout>
                            <Box>{renderLimit()}</Box>
                            <Box>{renderSlotsLeft()}</Box>
                        </Box>
                    </TooltipContent>
                    <TooltipTrigger className={style.dataBearingIcon}>
                        <DataBearing />
                    </TooltipTrigger>
                </Tooltip>
            ) : (
                <>
                    <Box marginBottom={"Xs"} justifyContent={"SpaceBetween"}>
                        <Box
                            horizontalAlignment={"Center"}
                            verticalAlignment={"Center"}
                            justifyContent={"SpaceBetween"}
                        >
                            {isLimitHit() ? (
                                <ErrorIcon size={16} className={style.dataBearingSummaryIcon} />
                            ) : isLimitApproaching(remaining, trackingSummary.trackingLimit) ? (
                                <Warning size={16} className={style.dataBearingSummaryIcon} />
                            ) : null}
                            <Heading variant={"SUBTITLE_2"}>{t("AssetManagement.navigationSidePanel.title")}</Heading>
                        </Box>
                        <IconButton
                            testId={testIds.navigation.assets.dataBearingSummary.expandButton}
                            onClick={() => {
                                setExpanded((prevState) => !prevState);
                            }}
                        >
                            {expanded ? <ArrowUp /> : <ArrowDown />}
                        </IconButton>
                    </Box>
                    {expanded ? (
                        <>
                            <ProgressBar
                                className={classNames(
                                    { [style.progressBarError]: isLimitHit() },
                                    {
                                        [style.progressBarWarning]: isLimitApproaching(
                                            remaining,
                                            trackingSummary.trackingLimit
                                        ),
                                    }
                                )}
                                testId={testIds.navigation.assets.dataBearingSummary.progressBarLabel}
                                label={renderLimit()}
                                progress={calculatePercentage()}
                            />
                            <Caption
                                greyTextStyle={
                                    !isLimitApproaching(remaining, trackingSummary.trackingLimit) && !isLimitHit()
                                }
                                errorStyle={isLimitHit()}
                                className={
                                    isLimitApproaching(remaining, trackingSummary.trackingLimit)
                                        ? style.progressBarColorWarning
                                        : undefined
                                }
                            >
                                {renderSlotsLeft()}
                            </Caption>
                        </>
                    ) : null}
                </>
            )}
        </Box>
    );
};

export default AssetsSummaryFooter;
